import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { useIntl } from "react-intl";
import content from "../../constants/content";
import { STATIC_PATH } from "../../constants/config";

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BannerWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  width: 100%;
  box-sizing: border-box;
  animation: ${fadeSlideIn} 0.5s ease-out;
`;

const BannerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-radius: 20px;
  padding: 20px 24px;
  margin: 24px 0 0 12px;
  max-width: 1080px;
  width: calc(100% - 24px);
  box-shadow: 0px 12px 32px rgba(0, 0, 0, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-sizing: border-box;

  @media (max-width: 1136px) {
    margin: 24px auto 0;
    width: calc(100% - 24px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;
    margin: 16px auto 0;
    width: calc(100% - 24px);
  }
`;

const BannerLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BannerTitle = styled.h2`
  font-family: "VM-Poppins", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  padding: 0;
  line-height: 1.2;
`;

const BannerSubtitle = styled.div`
  display: flex;
  align-items: center;
  font-family: "VM-Poppins", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  line-height: 1.5;

  @media (max-width: 480px) {
    align-items: flex-start;
  }
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(90deg, #ff7e40 0%, #ff451a 100%);
  color: #ffffff;
  border: none;
  border-radius: 100px;
  padding: 12px 24px;
  font-family: "VM-Poppins", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0px 4px 12px rgba(255, 69, 26, 0.2);
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 6px 16px rgba(255, 69, 26, 0.3);
    filter: brightness(1.05);
  }

  &:active {
    transform: translateY(0);
    filter: brightness(0.95);
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const BANNER_TYPE = {
  DUMMY_PROJECT: "dummy",
  NO_AUDIO: "noAudio",
  NO_TRANSCRIPTION: "noTranscription",
};

const getBannerConfig = (bannerType) => {
  const configs = {
    [BANNER_TYPE.DUMMY_PROJECT]: {
      title: "FREE_CREDITS_EXHAUSTED",
      subtitle: "2 shorts are predicted to perform exceptionally well. Upgrade to view and publish your shorts.",
      buttonText: "Unlock Shorts",
      icon: "video-clip.svg",
      showButton: true,
    },
    [BANNER_TYPE.NO_AUDIO]: {
      title: "🔇 No speech detected",
      subtitle: "We processed your video, but voiceovers help us create more meaningful shorts.",
      buttonText: "Add Audio",
      icon: "video-clip.svg",
      showButton: false,
    },
    [BANNER_TYPE.NO_TRANSCRIPTION]: {
      title: "🔇 No speech detected",
      subtitle: "We processed your video, but voiceovers help us create more meaningful shorts.",
      buttonText: "Add Transcription",
      icon: "video-clip.svg",
      showButton: false,
    },
  };
  return configs[bannerType] || null;
};

const ShortsListBanner = ({
  isDummyProject,
  hasNoAudio,
  hasNoTranscription,
  onUpgradeClick,
  sourceType,
  isReady,
}) => {
  const intl = useIntl();

  const activeBannerType = useMemo(() => {
    if (isDummyProject) {
      return BANNER_TYPE.DUMMY_PROJECT;
    }
    if (hasNoAudio) {
      return BANNER_TYPE.NO_AUDIO;
    }
    if (hasNoTranscription) {
      return BANNER_TYPE.NO_TRANSCRIPTION;
    }
    return null;
  }, [isDummyProject, hasNoAudio, hasNoTranscription]);

  const config = useMemo(() => getBannerConfig(activeBannerType), [activeBannerType]);

  const titleText = useMemo(() =>
    activeBannerType === BANNER_TYPE.DUMMY_PROJECT
      ? intl.formatMessage({ id: content.FREE_CREDITS_EXHAUSTED })
      : config?.title,
    [activeBannerType, intl, config?.title]
  );

  // Don't render anything until the parent confirms real data has loaded.
  // Once it mounts, BannerWrapper's `animation` (fadeSlideIn) plays automatically,
  // giving a smooth entrance instead of an abrupt pop-in.
  if (!isReady || !sourceType || !config) return null;

  return (
    <BannerWrapper key={activeBannerType}>
      <BannerContainer>
        <BannerLeft>
          <BannerTitle>{titleText}</BannerTitle>
          <BannerSubtitle>
            <img src={`${STATIC_PATH}${config.icon}`} alt="" style={{ marginRight: '8px', flexShrink: 0, width: '18px', height: '18px' }} />
            <span>{config.subtitle}</span>
          </BannerSubtitle>
        </BannerLeft>
        {config.showButton && (
          <UpgradeButton onClick={onUpgradeClick}>
            <img src={`${STATIC_PATH}white-star.svg`} alt="" />
            {config.buttonText}
          </UpgradeButton>
        )}
      </BannerContainer>
    </BannerWrapper>
  );
};

ShortsListBanner.propTypes = {
  isDummyProject: PropTypes.bool,
  hasNoAudio: PropTypes.bool,
  hasNoTranscription: PropTypes.bool,
  onUpgradeClick: PropTypes.func,
  sourceType: PropTypes.string,
  isReady: PropTypes.bool,
};

ShortsListBanner.defaultProps = {
  isDummyProject: false,
  hasNoAudio: false,
  hasNoTranscription: false,
  onUpgradeClick: () => {},
  isReady: false,
};

export default ShortsListBanner;